import { useCallback, useEffect, useState } from 'react';
import { BlockerFunction, useBlocker } from 'react-router-dom';

type UnsavedChangesModalOptions = {
  /**
   * When true, in-app navigations that keep the same pathname (e.g. a tab switch that only
   * changes search params) are not blocked. The `beforeunload` guard still fires on real
   * unload while `shouldBlock` is true.
   */
  ignoreSearchParamChanges?: boolean;
};

/**
 * Hook that provides custom modal for navigation blocking instead of browser-style dialogs
 * @param {boolean} shouldBlock - Whether to block navigation
 * @param {UnsavedChangesModalOptions} options - Fine-tunes which navigations are blocked
 */
export const useUnsavedChangesModal = (shouldBlock: boolean, options?: UnsavedChangesModalOptions) => {
  const ignoreSearchParamChanges = options?.ignoreSearchParamChanges ?? false;
  const [showModal, setShowModal] = useState(false);
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => {
        if (!shouldBlock) {
          return false;
        }

        if (ignoreSearchParamChanges && currentLocation.pathname === nextLocation.pathname) {
          return false;
        }

        return true;
      },
      [shouldBlock, ignoreSearchParamChanges],
    ),
  );

  useEffect(() => {
    if (!shouldBlock && blocker.state === 'blocked') {
      blocker.proceed?.();
    }
  }, [blocker, shouldBlock]);

  useEffect(() => {
    if (blocker.state === 'blocked' && shouldBlock) {
      setShowModal(true);
    }
  }, [blocker, shouldBlock]);

  const handleConfirm = useCallback(() => {
    setShowModal(false);
    if (blocker?.proceed) {
      blocker.proceed();
    }
  }, [blocker]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    blocker.reset?.();
  }, [blocker]);

  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  return {
    modalProps: {
      open: showModal,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  };
};
